import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import { Elements } from 'react-stripe-elements';
import FormSelect from 'components/common/FormSelect';
import LoadingAnimation from 'components/common/LoadingAnimation';
import InjectedCheckoutForm from './InjectedCheckoutForm';

// Restyled to match the "panda" design system's modal shell
// (see panda-ui/components/ItemModal.js) instead of react-bootstrap's
// <Modal>. Only presentational - props/behavior are unchanged.
const StripeFormModal = ({
  show,
  onClose,
  orderHandler,
  loadingHandler,
  totalPrice,
  cateringOrder,
  clearCateringOrder,
  setSaveCard,
  selectedStripePaymentMethod
}) => {
  const [active, setActive] = useState(null);
  useEffect(() => {
    if (selectedStripePaymentMethod) {
      setActive(selectedStripePaymentMethod)
    }
  }, [selectedStripePaymentMethod]);
  useEffect(() => {
    return () => clearCateringOrder();
  }, []);
  useEffect(() => {
    if (cateringOrder && !cateringOrder?.savedCards?.length) {
      setActive('add');
    }
  }, [cateringOrder]);

  const handleSubmit = () => {
    if (active && cateringOrder.purchaseId !== 'add') {
      orderHandler(active);
    }
  };

  const savedCardsOptions = cateringOrder?.savedCards?.map(item => ({
    label: item.name,
    value: item.paymentMethodId,
  }));

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-[#202124]/70 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full border border-panda-tint p-6 sm:p-7 max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 w-8 h-8 rounded-full bg-gray-100 text-gray-500 hover:bg-[#E53935] hover:text-white flex items-center justify-center transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <h3 className="font-heading font-bold text-lg text-[#202124] pr-6">
          Update Payment Method
        </h3>
        <div className="mt-5">
          {cateringOrder?.savedCards?.length ? (
            <FormSelect
              name="numPeopleRange"
              placeholder="Pay with"
              className="mb-5"
              value={savedCardsOptions?.find((item) => item?.value == active)}
              options={[
                ...savedCardsOptions,
                { label: 'Add new card', value: 'add' },
              ]}
              onChange={(_, value) => setActive(value)}
            />
          ) : null}
          {!cateringOrder ? <LoadingAnimation /> : null}
          {active === 'add' && cateringOrder ? (
            <Elements>
              <InjectedCheckoutForm
                {...{
                  orderHandler,
                  loadingHandler,
                  totalPrice,
                  setSaveCard,
                }}
              />
            </Elements>
          ) : (
            <button
              type="button"
              disabled={!active}
              onClick={handleSubmit}
              className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-panda-red to-panda-dark hover:from-panda-dark hover:to-panda-dark text-white font-heading font-bold text-sm shadow-md shadow-panda-red/30 active:scale-98 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Save Payment Method
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

StripeFormModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  orderHandler: PropTypes.func,
  loadingHandler: PropTypes.func,
  setSaveCard: PropTypes.func,
  totalPrice: PropTypes.number.isRequired,
  clearCateringOrder: PropTypes.func.isRequired,
  cateringOrder: PropTypes.shape({
    savedCards: PropTypes.arrayOf(
      PropTypes.objectOf({
        name: PropTypes.string,
        payment_method_id: PropTypes.string,
      }),
    ),
    clientSecret: PropTypes.string,
    purchaseId: PropTypes.number.isRequired,
  }).isRequired,
};

StripeFormModal.defaultProps = {
  show: true,
  onClose: () => { },
  orderHandler: () => { },
  loadingHandler: () => { },
  setSaveCard: () => { },
};

export default StripeFormModal;
